import { DocumentClassificationType, DocumentRecord } from '@/models/DocumentRecord';

type SellerDisclosureLogicalForm = NonNullable<
  NonNullable<DocumentRecord['sellerDisclosureManifest']>['forms']
>[number];

export type SellerDisclosureDisplayRecord = DocumentRecord & {
  displayKey: string;
  logicalForm?: SellerDisclosureLogicalForm;
};

export type SellerDisclosureViewerForm = SellerDisclosureLogicalForm & {
  displayKey: string;
  sourceDocumentRecordId: string;
};

/**
 * Recovers the familiar printed C.A.R. form code from an uploaded filename
 * when a processed logical-form manifest is not available yet. This is a
 * presentation fallback only; Pulse remains authoritative once processing
 * supplies the manifest.
 */
const CAR_FORM_ACRONYMS_BY_TITLE: [RegExp, string][] = [
  [/\bprds\b|san\s+mateo\s+santa\s+clara\s+counties\s+advisory/i, 'PRDS'],
  [/\bbhia\b|buyer'?s?\s+homeowners?'?\s+insurance\s+advisory/i, 'BHIA'],
  [/sellers?'?\s+affidavit\s+of\s+non-?foreign\s+status|firpta/i, 'AS'],
  [/wire\s+fraud\s+advisory/i, 'WFA'],
  [/buyer'?s?\s+investigation\s+advisory/i, 'BIA'],
];

export const getCarFormAcronym = (filename: string | undefined): string | undefined => {
  if (!filename) return undefined;
  return CAR_FORM_ACRONYMS_BY_TITLE.find(([pattern]) => pattern.test(filename))?.[1];
};

/** True once at least one seller-disclosure upload has finished Pulse processing. */
export const hasProcessedSellerDisclosures = (records: DocumentRecord[] | undefined): boolean =>
  (records ?? []).some(
    (record) =>
      record.documentType === DocumentClassificationType.SELLER_DISCLOSURE &&
      record.sellerDisclosureManifest,
  );

const normalizeLegacyLogicalForm = (
  logicalForm: SellerDisclosureLogicalForm,
): SellerDisclosureLogicalForm => {
  const label = `${logicalForm.title} ${logicalForm.shortLabel}`;

  // Manifests written before DSDT joined the seller-disclosure registry kept
  // Pulse's printed, all-caps title under UNKNOWN_SELLER_DISCLOSURE. Normalize
  // those persisted records at the presentation boundary so they use the same
  // acronym chip, title casing, grouping, and styling as newly processed DSDT
  // forms. Reprocessing will persist the canonical values, but is not required
  // merely to render the bookmark correctly.
  if (/\bdefensible\s+space\s+decision\s+tree\b/i.test(label)) {
    return {
      ...logicalForm,
      formKey: 'DSDT',
      shortLabel: 'DSDT',
      title: 'Defensible Space Decision Tree',
    };
  }

  // Older manifests stored Hauser's descriptive internal key even though the
  // printed Parking and Storage Disclosure form code is PSD. Normalize at the
  // presentation boundary so existing packets gain the acronym immediately;
  // the canonical registry now persists PSD for newly processed packets.
  if (logicalForm.formKey === 'PARKING_STORAGE_DISCLOSURE') {
    return {
      ...logicalForm,
      formKey: 'PSD',
      shortLabel: 'PSD',
    };
  }

  return logicalForm;
};

/**
 * Expands one physical seller-disclosure PDF into its Pulse-detected logical
 * form chips without inventing additional DocumentRecords.
 */
export const buildSellerDisclosureDisplayRecords = (
  records: DocumentRecord[],
): SellerDisclosureDisplayRecord[] =>
  records
    .filter((record) => record.documentType === DocumentClassificationType.SELLER_DISCLOSURE)
    .flatMap((record) => {
      const logicalForms = (record.sellerDisclosureManifest?.forms ?? []).map(
        normalizeLegacyLogicalForm,
      );
      return logicalForms.length > 0
        ? logicalForms.map((logicalForm) => ({
            ...record,
            logicalForm,
            displayKey: `${record.id}:${logicalForm.formKey}:${logicalForm.primaryPage}`,
          }))
        : [{ ...record, displayKey: record.id }];
    });

/**
 * Builds one bookmark list across every physical seller-disclosure upload.
 * A compiled packet contributes many forms from one record; a packet uploaded
 * as separate PDFs contributes forms from many records. The source record ID
 * keeps each bookmark linked to the correct PDF as well as the correct page.
 */
export const buildSellerDisclosureViewerForms = (
  records: DocumentRecord[],
): SellerDisclosureViewerForm[] =>
  buildSellerDisclosureDisplayRecords(records).flatMap((record) =>
    record.logicalForm
      ? [
          {
            ...record.logicalForm,
            displayKey: record.displayKey,
            sourceDocumentRecordId: record.id,
          },
        ]
      : [],
  );

/** PDF viewers interpret the fragment locally; the authorized presigned URL is unchanged. */
export const withPdfPage = (url: string, page?: number): string =>
  Number.isInteger(page) && Number(page) > 0 ? `${url}#page=${page}` : url;

// Real C.A.R. form codes are short, clean, all-caps (TDS, SPQ, NHD, WCMD) —
// Hauser's own internal composite keys for forms without a snappy printed
// code are long, underscored, and would look broken as a chip (e.g.
// "ENVIRONMENTAL_HAZARDS_ACKNOWLEDGMENT"). shortLabel is unreliable here —
// for TDS/SPQ/DIA it's literally just the code again, not a description —
// so this only checks whether formKey itself looks like a real short code,
// and always pairs it with the form's title, which is always a real
// description.
export const looksLikeRealAcronym = (formKey: string): boolean =>
  !formKey.includes('_') && formKey.length <= 6;

const PRINTED_FORM_ACRONYM = /^[A-Z][A-Z0-9-]{1,9}$/;

/**
 * Internal registry keys are not always the printed form code. For example,
 * TRUST_ADVISORY and RCSD_S deliberately use descriptive TypeScript-safe keys
 * while their canonical printed labels are TA and RCSD-S.
 */
export const getSellerDisclosureAcronym = (form: {
  formKey: string;
  shortLabel: string;
}): string | undefined => {
  if (looksLikeRealAcronym(form.formKey)) return form.formKey;
  return PRINTED_FORM_ACRONYM.test(form.shortLabel) ? form.shortLabel : undefined;
};
